<template>
  <div class="inline-block w-full">
    <input
      :id="id"
      type="checkbox"
      :checked="modelValue === value"
      :disabled="disabled"
      :name="name"
      :value="value"
      tabindex="0"
      :required="required"
      :aria-labelledby="`${id}-label`"
      :aria-invalid="!!error"
      :aria-describedby="
        error ? `${id}-error` : helpLabel ? `${id}-help` : undefined
      "
      class="peer sr-only"
      @click="updateValue"
      @keydown.space.prevent="updateValue"
      @keydown.enter.prevent="updateValue"
    />
    <label
      :id="`${id}-label`"
      :for="id"
      :class="[
        'flex items-start gap-3',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      ]"
    >
      <span
        :class="[
          'checkbox-container w-5 h-5 relative shrink-0 border border-0.5 rounded-xs transition-colors duration-200 ease-in-out mt-0.5',
          modelValue == value && !disabled && 'bg-red-1 border-transparent',
          modelValue != value && !disabled ? 'bg-white dark:bg-dark-surface border-grey-11 dark:border-dark-border' : '',
          modelValue != value && disabled ? 'bg-white dark:bg-dark-surface border-grey-12 dark:border-dark-border' : '',
          modelValue == value && disabled ? 'bg-grey-8 dark:bg-dark-border border-transparent' : '',
          !disabled && 'hover:border-red-1',
        ]"
        aria-hidden="true"
      >
        <span class="absolute inset-0 flex items-center justify-center">
          <mdicon
            v-if="modelValue === value"
            size="18"
            name="check"
            class="text-white"
            aria-hidden="true"
          />
        </span>
      </span>
      <span
        class="text-base font-medium flex-1 min-w-0 break-words dark:text-dark-ink"
        :class="[disabled ? 'text-grey-11 dark:text-dark-ink-muted' : '']"
      >
        {{ label }} <AppRequiredLabel v-if="required" />
      </span>
    </label>
    <AppErrorLabel v-if="error" :id="`${id}-error`" class="mt-1"
      >{{ error }}
    </AppErrorLabel>
    <AppHelpLabel v-if="helpLabel" :id="`${id}-help`" class="mt-1"
      >{{ helpLabel }}
    </AppHelpLabel>
  </div>
</template>

<script setup>
const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  helpLabel: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
  value: {
    type: [String, Number, Boolean],
    default: '',
  },
  allowUnselect: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
  },
  required: { type: Boolean, default: false },
  name: {
    type: String,
    default: () => `checkbox-${Math.random().toString(36).substr(2, 9)}`,
  },
  id: {
    type: String,
    default: () => `checkbox-${Math.random().toString(36).substr(2, 9)}`,
  },
})

const emit = defineEmits(['update:modelValue'])

const updateValue = () => {
  if (props.value == props.modelValue && props.allowUnselect) {
    emit('update:modelValue', null)
  } else {
    emit('update:modelValue', props.value)
  }
}
</script>

<style>
@reference "../../../../css/app.css";
.checkbox-container:not(.disabled):hover::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 50%;
}

input:focus-visible + label .checkbox-container {
  @apply ring-2 ring-red-1 ring-offset-2;
}
</style>
